import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { useVenueMarket } from "@/hooks/useVenueMarket";
import { Trophy, ArrowRight } from "lucide-react";

const TIER_COLORS = {
  bronze:   { bar: "bg-amber-700", text: "text-amber-900", bg: "bg-amber-50",   border: "border-amber-200" },
  silver:   { bar: "bg-zinc-400",  text: "text-zinc-900",  bg: "bg-zinc-50",    border: "border-zinc-300" },
  gold:     { bar: "bg-yellow-500",text: "text-yellow-900",bg: "bg-yellow-50",  border: "border-yellow-300" },
  platinum: { bar: "bg-indigo-600",text: "text-indigo-900",bg: "bg-indigo-50",  border: "border-indigo-300" },
};

/**
 * Compact Partner Dividends card for the Overview page. Shows the venue's
 * live tier + progress bar + accumulated dividend earned so far.
 */
export default function PartnerDividendsCard() {
  const [status, setStatus] = useState(null);
  const { fmt } = useVenueMarket();

  useEffect(() => {
    let cancelled = false;
    http.get("/venues/me/dividends")
      .then((r) => { if (!cancelled) setStatus(r.data); })
      .catch(() => { /* keep skeleton */ });
    return () => { cancelled = true; };
  }, []);

  if (!status) return null;
  const tier = status.current_tier;
  const next = status.next_tier;
  const c = TIER_COLORS[tier.key] || TIER_COLORS.bronze;
  const tickets = status.tickets_since_baseline;
  // Progress fills within the *current* tier band (min → max).
  const spanLo = tier.min;
  const spanHi = tier.max ?? tier.min + 1;
  const pct = tier.max
    ? Math.min(100, Math.round(((tickets - spanLo) / (spanHi - spanLo)) * 100))
    : 100;
  const ratePct = (tier.rate_bp / 100).toFixed(tier.rate_bp % 100 === 0 ? 0 : 1);

  return (
    <Link
      to="/dashboard/dividends"
      data-testid="partner-dividends-card"
      className={`block rounded-2xl border-2 ${c.border} ${c.bg} p-5 transition hover:shadow-lg`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <Trophy className={`w-4 h-4 ${c.text}`} />
          <span className={`text-xs uppercase tracking-widest font-mono ${c.text}`}>Partner Dividends</span>
        </div>
        <ArrowRight className={`w-4 h-4 ${c.text} opacity-60`} />
      </div>
      <div className="mt-3 flex items-baseline gap-2">
        <span className={`font-display font-black text-2xl tracking-tighter ${c.text}`}>{tier.label}</span>
        <span className={`text-xs ${c.text} opacity-80`}>· {ratePct}% of platform fee</span>
      </div>
      <div className="mt-3 h-2 rounded-full bg-white overflow-hidden">
        <div className={`h-full ${c.bar} transition-all duration-500`} style={{ width: `${pct}%` }} />
      </div>
      <div className="mt-2 flex items-center justify-between text-[11px]">
        <span className={c.text} data-testid="dividends-accrued">
          {fmt(status.accumulated_dividend_pence)} earned
        </span>
        <span className={c.text}>
          {next ? `${next.tickets_to_go.toLocaleString()} to ${next.label}` : "Top tier"}
        </span>
      </div>
    </Link>
  );
}
